import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import projects from '../../data/projects';
import personalData from '../../data/personal';

const ProjectMedia = ({ project, eager = false }) => {
  const { visual } = project;

  if (visual.kind === 'phones') {
    return (
      <div className="project-media__phones">
        {visual.images.map((image, index) => (
          <div key={image.src} className={`project-media__phone project-media__phone--${index + 1}`}>
            <img
              src={image.src}
              alt={image.alt}
              loading={eager ? 'eager' : 'lazy'}
              draggable="false"
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="project-media__desktop">
      <div className="project-media__browser">
        <div className="project-media__bar" aria-hidden="true">
          <span />
          <span />
          <span />
          <small>{visual.label}</small>
        </div>
        <img
          src={visual.images[0].src}
          alt={visual.images[0].alt}
          loading={eager ? 'eager' : 'lazy'}
          draggable="false"
        />
      </div>
      {visual.images[1] && (
        <div className="project-media__secondary">
          <img
            src={visual.images[1].src}
            alt={visual.images[1].alt}
            loading="lazy"
            draggable="false"
          />
        </div>
      )}
    </div>
  );
};

const ProjectCard = ({ project, index }) => {
  const isWide = index === 0;

  return (
    <motion.article
      className={`project-case ${isWide ? 'project-case--wide lg:col-span-2' : ''}`}
      style={{
        '--case-accent': project.accent,
        '--case-accent-rgb': project.accentRgb,
      }}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.58, delay: Math.min(index * 0.04, 0.2), ease: [0.19, 1, 0.22, 1] }}
    >
      <div className="project-case__media">
        <ProjectMedia project={project} eager={index === 0} />
      </div>

      <div className="project-case__copy">
        <div className="flex items-start justify-between gap-4">
          <p className="project-case__type">{project.type}</p>
          <span className="project-case__index">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <h3>{project.title}</h3>
        <p className="project-case__description">{project.description}</p>

        <div className="project-case__stack">
          {project.stack.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        {project.url && (
          <a href={project.url} target="_blank" rel="noopener noreferrer">
            Visit live product
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </motion.article>
  );
};

const Projects = () => (
  <section id="work" className="section-padding bg-[#eef3f8]">
    <div className="container-wrapper">
      <motion.div
        className="mb-12 flex flex-col justify-between gap-6 lg:mb-14 lg:flex-row lg:items-end"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
      >
        <div>
          <p className="section-label mb-3">Selected systems</p>
          <h2 className="max-w-3xl text-3xl font-black tracking-normal text-[#191922] sm:text-4xl md:text-5xl">
            Real products. Real operating screens.
          </h2>
        </div>
        <p className="max-w-md text-sm leading-7 text-slate-600 sm:text-base">
          Product work across automotive operations, finance, housing, reporting, marketplaces,
          community services, and launch systems.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {projects.map((project, index) => (
          <ProjectCard key={project.id} project={project} index={index} />
        ))}
      </div>

      <motion.div
        className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-slate-300 pt-8 sm:flex-row sm:items-center"
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
      >
        <p className="text-sm text-slate-600">Have a product or operational workflow in mind?</p>
        <a
          href={personalData.calendly}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary group"
        >
          Book a 1:1 call
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </a>
      </motion.div>
    </div>
  </section>
);

export default Projects;
